import { Clock4, CheckCircle2, Loader2, ShieldCheck, Send, Banknote } from "lucide-react";

const TIMELINE = [
    { icon: Loader2, label: "Processing", time: "0m", state: "done" },
    { icon: ShieldCheck, label: "Compliance Check", time: "8m", state: "done" },
    { icon: Send, label: "Payment Sent", time: "1h 12m", state: "active" },
    { icon: Banknote, label: "INR Credited", time: "~22h", state: "pending" },
];

export default function SettlementPromise() {
    return (
        <section
            data-testid="settlement-promise-section"
            className="relative bg-gradient-to-b from-zinc-50 to-white py-24 md:py-32 px-4 md:px-8"
        >
            <div className="mx-auto max-w-7xl">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                    <div className="lg:col-span-5">
                        <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 text-emerald-700 px-3 py-1 text-[11px] tracking-[0.25em] uppercase font-bold">
                            <Clock4 className="h-3.5 w-3.5" />
                            Settlement promise
                        </span>
                        <h2 className="font-heading mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-zinc-950 leading-[1.05]">
                            INR credited within{" "}
                            <span className="text-gradient-emerald">24 hours</span>.
                        </h2>
                        <p className="mt-5 text-zinc-600 max-w-md">
                            We track every leg of the payment from initiation to your
                            beneficiary's account — so you never have to wonder "where is my
                            payment?".
                        </p>
                        <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
                            <div>
                                <div className="font-heading text-2xl sm:text-3xl font-extrabold text-zinc-950 font-mono">
                                    24h
                                </div>
                                <div className="text-xs text-zinc-500 mt-1">avg INR credit</div>
                            </div>
                            <div>
                                <div className="font-heading text-2xl sm:text-3xl font-extrabold text-zinc-950 font-mono">
                                    99.6%
                                </div>
                                <div className="text-xs text-zinc-500 mt-1">on-time SLA</div>
                            </div>
                            <div>
                                <div className="font-heading text-2xl sm:text-3xl font-extrabold text-zinc-950 font-mono">
                                    0
                                </div>
                                <div className="text-xs text-zinc-500 mt-1">hidden fees</div>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        <div className="rounded-3xl border border-zinc-200 bg-white shadow-[0_30px_100px_-40px_rgba(6,78,59,0.35)] p-6 sm:p-8">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-xs tracking-widest uppercase text-zinc-400 font-bold">
                                        Live transaction · TXN-9182
                                    </div>
                                    <div className="mt-1 font-heading text-xl sm:text-2xl font-bold text-zinc-950 font-mono">
                                        $10,000 · USD → INR
                                    </div>
                                </div>
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 px-3 py-1 text-xs font-bold">
                                    <span className="h-2 w-2 rounded-full bg-emerald-500 animate-flow-pulse" />
                                    In-flight
                                </span>
                            </div>

                            <ol className="mt-8 space-y-5">
                                {TIMELINE.map((t, i) => {
                                    const isDone = t.state === "done";
                                    const isActive = t.state === "active";
                                    return (
                                        <li
                                            key={t.label}
                                            data-testid={`settlement-step-${i}`}
                                            className="relative pl-12"
                                        >
                                            {i < TIMELINE.length - 1 && (
                                                <span
                                                    aria-hidden
                                                    className={`absolute left-[19px] top-9 bottom-[-26px] w-px ${
                                                        isDone
                                                            ? "bg-emerald-400"
                                                            : "bg-zinc-200"
                                                    }`}
                                                />
                                            )}
                                            <span
                                                className={`absolute left-0 top-0 inline-flex h-10 w-10 items-center justify-center rounded-full ${
                                                    isDone
                                                        ? "bg-emerald-600 text-white"
                                                        : isActive
                                                          ? "bg-amber-100 text-amber-700 ring-2 ring-amber-300"
                                                          : "bg-zinc-100 text-zinc-400"
                                                }`}
                                            >
                                                {isDone ? (
                                                    <CheckCircle2 className="h-5 w-5" />
                                                ) : (
                                                    <t.icon
                                                        className={`h-5 w-5 ${
                                                            isActive ? "animate-spin" : ""
                                                        }`}
                                                    />
                                                )}
                                            </span>
                                            <div className="flex items-center justify-between gap-3">
                                                <div>
                                                    <div className="text-sm sm:text-base font-bold text-zinc-950">
                                                        {t.label}
                                                    </div>
                                                    <div className="text-xs text-zinc-500">
                                                        {isDone
                                                            ? "Completed"
                                                            : isActive
                                                              ? "In progress"
                                                              : "Estimated"}
                                                    </div>
                                                </div>
                                                <span className="font-mono text-xs sm:text-sm text-zinc-500">
                                                    {t.time}
                                                </span>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ol>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
